import { Button, Modal, SelectMenuStandard, TextInput } from "@truevault/aquilo";
import React, { useEffect, useState } from "react";
import { Field, Form } from "react-final-form";
import { Loading } from "../../../common/components/Loading";
import { useActionRequest } from "../../../common/hooks/api";
import { Api } from "../../../common/service/Api";
import { OrganizationPublicId, UserRole } from "../../../common/service/server/Types";
import { Error as RequestsError, Success as RequestsSuccess } from "../../copy/requests";
import { requiredEmail, requiredString } from "../../util/validation";
import { formatUserRole } from "../../util/formatters";
import { Error } from "../Error";
import { OrganizationUserDto } from "../../../common/service/server/dto/UserDto";

const inviteUserValidation = (values, existingUserEmails?: Set<string>) => {
  const errors = {} as any;

  requiredEmail(values, "email", errors);
  if (values.email?.toLowerCase().startsWith("privacy@")) {
    errors.email = 'Email addresses starting with "privacy@" are not allowed';
  }
  requiredString(values, "firstName", errors);
  requiredString(values, "lastName", errors);

  if (values.email && existingUserEmails?.has(values.email.toLowerCase())) {
    errors.email = "This user already has access to this organization";
  }

  return errors;
};

const inviteUserInitialValues = { email: "" };

const helperTextForRole = (role: UserRole): string => {
  switch (role) {
    case "HR":
      return "This user will only have access to HR onboarding and HR request data.";
    case "GENERAL":
      return "This user will have full access to Polaris, excluding HR information.";
    case "ADMIN":
      return "This user will have full access to Polaris.";
  }
  return "";
};

const TextInputField = ({
  field,
  label,
  type = "text",
  autoFocus = false,
}: {
  field: string;
  label: string;
  type?: string;
  autoFocus?: boolean;
}) => (
  <Field name={field}>
    {({ meta }) => (
      <div className="w-full">
        <TextInput field={field} label={label} type={type} autoFocus={autoFocus} required />
        {meta.touched && meta.error && <p className="text-red-800 text-[12px]">{meta.error}</p>}
      </div>
    )}
  </Field>
);

const InviteUserForm = ({
  handleSubmit,
  onCancel,
  onlyShowRole,
  helpText,
  allowAddingHrUser,
  currentRole,
  onRoleChange,
}: {
  handleSubmit: (e?: React.FormEvent) => void;
  onCancel: () => void;
  onlyShowRole?: UserRole;
  helpText?: string;
  allowAddingHrUser: boolean;
  currentRole: UserRole;
  onRoleChange: (role: UserRole) => void;
}) => {
  const roleOptions = [
    { value: "GENERAL", label: "General" },
    { value: "ADMIN", label: "Admin" },
    ...(allowAddingHrUser ? [{ value: "HR", label: "HR" }] : []),
  ];

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-[12px]">
      <div className="flex gap-[12px]">
        <TextInputField autoFocus field="firstName" label="First Name" />
        <TextInputField field="lastName" label="Last Name" />
      </div>
      <div>
        <div className="flex gap-[12px]">
          <TextInputField field="email" label="Email Address" type="email" />
          {onlyShowRole === undefined && (
            <div className="shrink-0 w-[160px]">
              <label
                htmlFor="addUserRoleSelect"
                className="block text-sm font-medium leading-6 text-gray-900"
              >
                Role
              </label>
              <SelectMenuStandard
                id="addUserRoleSelect"
                items={roleOptions}
                selectedValue={currentRole}
                onSelect={(value) => onRoleChange(value as UserRole)}
                height="40px"
              />
            </div>
          )}
        </div>
        <p className="mt-[6px] text-sm text-neutral-500">
          {helpText || helperTextForRole(onlyShowRole || currentRole)}
        </p>
      </div>
      <div className="flex justify-end items-center gap-[12px]">
        <Button color="tertiary" onClick={onCancel}>
          Cancel
        </Button>
        <Button color="primary" type="submit" onClick={() => {}}>
          {onlyShowRole ? `Add ${formatUserRole(onlyShowRole)} User` : "Add User"}
        </Button>
      </div>
    </form>
  );
};

type InviteUserDialogProps = {
  open: boolean;
  organizationId: OrganizationPublicId;
  onlyShowRole?: UserRole;
  helpText?: string;
  onClose: () => void;
  onDone: (invitedUser: OrganizationUserDto) => void;
  allowAddingHrUser?: boolean;
  showDefaultSnackbarNotifications?: boolean;
  existingUserEmails?: Set<string>;
};

export const InviteUserDialog = ({
  open,
  organizationId,
  onlyShowRole,
  helpText,
  onClose,
  onDone,
  allowAddingHrUser = true,
  showDefaultSnackbarNotifications = true,
  existingUserEmails,
}: InviteUserDialogProps) => {
  const [currentRole, setCurrentRole] = useState<UserRole>("GENERAL");

  const {
    request,
    fetch: sendInviteRequest,
    resetState,
  } = useActionRequest({
    api: (values) => Api.organizationUser.inviteUser(organizationId, values),
    messages: showDefaultSnackbarNotifications
      ? {
          success: RequestsSuccess.InviteUser,
          defaultError: RequestsError.InviteUser,
        }
      : undefined,
    notifyError: true,
    notifySuccess: true,
    onSuccess: (result) => {
      onDone(result);
    },
    onError: (networkRequestState, error) => {
      networkRequestState.errorMessage =
        // @ts-ignore
        error?.response?.data?.message ??
        error?.message ??
        "There was an error while processing your request.";
    },
  });

  const onSubmit = async (values) => {
    values.role = onlyShowRole ?? currentRole;

    await sendInviteRequest(values);
  };

  useEffect(() => {
    if (open) {
      resetState();
    }
  }, [open, resetState]);

  if (!open) return null;

  return (
    <Modal
      title={onlyShowRole ? `Add ${formatUserRole(onlyShowRole)} User` : "Add a User"}
      alignText="left"
      closeButton
      showOverlay
      onClose={onClose}
      contentContainerClasses="w-full"
      maxPanelWidth="max-w-[600px]"
    >
      {request.running && <Loading />}

      {request.error && (
        <div className="flex flex-col gap-[12px]">
          <Error message={request.errorMessage || RequestsError.InviteUser} />
          <div className="flex justify-end items-center">
            <Button color="tertiary" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      )}

      {!request.started && (
        <Form
          onSubmit={onSubmit}
          initialValues={inviteUserInitialValues}
          validate={(values) => inviteUserValidation(values, existingUserEmails)}
          render={({ handleSubmit, form }) => (
            <InviteUserForm
              handleSubmit={(e) => {
                const result = handleSubmit(e);
                if (result && typeof result.then === "function") {
                  return result.then(
                    () => form.reset(),
                    () => {},
                  );
                }
                return result;
              }}
              onCancel={onClose}
              onlyShowRole={onlyShowRole}
              helpText={helpText}
              allowAddingHrUser={allowAddingHrUser}
              currentRole={currentRole}
              onRoleChange={setCurrentRole}
            />
          )}
        />
      )}
    </Modal>
  );
};
